import { createTheme } from "@mui/material/styles";

// The theme colors and font where derived from the HWBP styling. Some components have their own color overrides, but most of the colors are defined here.
// Other style overrides in components are mostly for spacing and padding.
let Theme = createTheme({
    typography: {
        fontFamily: ["-apple-system", "BlinkMacSystemFont", '"Segoe UI"', '"Helvetica Neue"',"Roboto",  "Arial", "sans-serif", '"Apple Color Emoji"', '"Segoe UI Emoji"', '"Segoe UI Symbol"'].join(","),
    },
    palette: {
        primary: {
            main: "#1d9fd6", // HWBP Blue
            input: "#bfe1e9",
            dark: "#1b2f68",
            grey: "#97979b"
        },
        secondary: {
            main: "#39870b", // HWBP Green
            dark: "#9a9b64",
            white: "#ffffff",
        },
        tertiary: {
            main: "#ef0000", // HWBP Red
        },
        graphics: {
            primaryCyan: "#1d9fd6",
            primaryGrey: "#97979b",
            primaryNavy: "#1b2f68",
            secondarySkyBlue: "#77c5e6",
            secondaryGreen: "#39870b",
            secondaryLime: "#b9d750",
            secondaryBrown: "#9b4509",
            secondaryTan: "#c38f6c",
            secondaryOrange: "#ff9500",
            secondaryGrey: "#808080",
        },
        background: {
            default: "#e5e5e5", // Whitesmoke
            lightsmoke: "#f0f0f0", // Off-white
            white: "#ffffff", // White
        },
        text: {
            white: "#ffffff",
            black: "#000000",
            darkblue: "#1b2f68",
            headingMain: "#9a9b64",
        },
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1280,
            xl: 1800,
        },
    },
});

// Assign colours to certain specific components. If you're uncertain about components MUI name just 'console.log' the component and you'll find the name in type->render->muiName.
Theme = createTheme(Theme, {
    components: {
        MuiContainer: {
            defaultProps: {
                maxWidth: "lg",
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    display: "block",
                    padding: "20px",
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                containedPrimary: {
                    "&:hover": {
                        backgroundColor: Theme.palette.secondary.main,
                        color: Theme.palette.secondary.contrastText,
                    },
                },
                outlinedPrimary: {
                    "&:hover": {
                        backgroundColor: Theme.palette.secondary.main,
                        color: Theme.palette.secondary.contrastText,
                    },
                },
                normalPrimary: {
                    "&:hover": {
                        backgroundColor: Theme.palette.secondary.main,
                        color: Theme.palette.secondary.contrastText,
                    },
                },
                textPrimary: {
                    "&:hover": {
                        borderRadius: 20,
                        color: Theme.palette.primary.dark,
                    },
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                root: {
                    borderColor: Theme.palette.secondary.main,
                },
            },
        },
        MuiAccordion: {
            styleOverrides: {
                root: {
                    padding: 0,
                    border: 0,
                    borderRadius: 5,
                    "&.Mui-expanded": {
                        border: 5,
                        borderColor: "#000000",
                    },
                },
            },
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: {
                    borderRadius: 5,
                    backgroundColor: Theme.palette.primary.light,
                    color: Theme.palette.text.dark,
                    "&:hover, &.Mui-expanded": {
                        backgroundColor: Theme.palette.primary.dark,
                        color: Theme.palette.text.white,
                    },
                },
            },
        },
        MuiSvgIcon: {
            styleOverrides: {
                colorPrimary: {
                    color: Theme.palette.text.white,
                },
                colorSecondary: {
                    color: Theme.palette.primary.main,
                },
            },
        },
        MuiFilledInput: {
            styleOverrides: {
                root: {
                    backgroundColor: Theme.palette.primary.input,
                    border: "1px solid",
                    borderColor: Theme.palette.secondary.main,
                },
            },
            variants: [
                {
                    props: { disabled: true },
                    style: {
                        backgroundColor: "transparent",
                        border: 0,
                        boxShadow: "none",
                        // Ensure disabled text renders as normal text color
                        "&.Mui-disabled .MuiInputBase-input": {
                            WebkitTextFillColor: Theme.palette.text.black,
                            color: Theme.palette.text.black,
                        },
                        "&.Mui-disabled:before, &.Mui-disabled:after": {
                            borderBottom: "none",
                        },
                    },
                },
            ],
        },
        MuiOutlinedInput: {
            variants: [
                {
                    props: { disabled: true },
                    style: {
                        backgroundColor: "transparent",
                        // Remove the outline for a plain text look
                        "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                        },
                        // Ensure disabled text renders as normal text color
                        "&.Mui-disabled .MuiInputBase-input": {
                            WebkitTextFillColor: Theme.palette.text.black,
                            color: Theme.palette.text.black,
                        },
                    },
                },
            ],
        },

        MuiTextField: {
            defaultProps: {
                size: "small",
            },
            variants: [
                {
                    props: { "read-only": "true" },
                    style: {
                        backgroundColor: Theme.palette.secondary.main,
                    },
                },
            ],
        },
        // MuiSelect: {
        //     variants: [
        //         {
        //             props: { disabled: true },
        //             style: {
        //                 color: "red",
        //                 backgroundColor: Theme.palette.background.default,
        //                 fontSize: "5rem",
        //             },
        //         },
        //     ],
        // },
        MuiTableRow: {
            variants: [
                {
                    props: { headerRow: true },
                    style: {
                        color: Theme.palette.text.white,
                        backgroundColor: Theme.palette.secondary.main,
                    },
                },
                {
                    props: { odd: "false" },
                    style: {
                        backgroundColor: Theme.palette.secondary.light,
                    },
                },
                {
                    props: { odd: "true" },
                    style: {
                        backgroundColor: Theme.palette.secondary.dark,
                    },
                },
            ],
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    paddingTop: 8,
                    paddingBottom: 8,
                    paddingLeft: 4,
                    paddingRight: 4,
                },
            },
        },
    },

    typography: {
        // h3 is the main heading on the page. The size is responsive to the screen size.

        h3: {
            [Theme.breakpoints.down("xl")]: {
                fontSize: "2.4rem",
            },
            [Theme.breakpoints.up("xl")]: {
                fontSize: "3.0rem",
            },
            color: Theme.palette.primary.dark,
        },
        h4: {
            color: Theme.palette.primary.dark,
        },
        h5: {
            color: Theme.palette.primary.dark,
        },
        h6: {
            color: Theme.palette.primary.dark,
        },
        subtitle1: {
            color: Theme.palette.primary.dark,
        },
    },
});

export default Theme;
